
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import OutlineButton from '@/components/ui/OutlineButton.vue';
import Modal from '@/components/ui/Modal.vue';
import { ProposalAction } from '@/store/proposal/proposal.action';
import { SalePriceRow } from '@/store/proposal/proposal.module';

interface RemoveProduct {
  key: string;
  label: string;
  tab: string;
  rowIds: string[];
}

interface RemoveCarrier {
  supplierId: string;
  supplier: string;
  surchargeCount: number;
  products: RemoveProduct[];
}

export default defineComponent({
  name: 'ProposalProductsRemove',
  components: { Modal, OutlineButton },
  data() {
    return {
      modalVisibility: false,
      showConfirm: false,
      selectedCarriers: {} as Record<string, boolean>,
      selectedProducts: {} as Record<string, boolean>,
    };
  },
  computed: {
    ...mapGetters(['salePricesRows', 'returnsRows', 'surchargesRows']),
    carriers(): RemoveCarrier[] {
      const bySupplier: Record<string, RemoveCarrier> = {};

      const addRows = (rows: SalePriceRow[], tab: string): void => {
        rows.forEach((row) => {
          if (!row.supplierId) {
            return;
          }
          const carrier = bySupplier[row.supplierId] ||= {
            supplierId: row.supplierId,
            supplier: row.supplier,
            surchargeCount: 0,
            products: [],
          };
          const key = `${tab}:${row.productId}`;
          let product = carrier.products.find((p) => p.key === key);
          if (!product) {
            product = {
              key,
              tab,
              label: row.country ? `${row.product} (${row.country})` : row.product,
              rowIds: [],
            };
            carrier.products.push(product);
          }
          product.rowIds.push(row.id);
        });
      };

      addRows(this.salePricesRows, 'outbound');
      addRows(this.returnsRows, 'returns');
      this.surchargesRows.forEach((row: SalePriceRow) => {
        if (row.supplierId && bySupplier[row.supplierId]) {
          bySupplier[row.supplierId].surchargeCount += 1;
        }
      });

      return Object.values(bySupplier);
    },
    selection(): { rowIds: string[]; fullSupplierIds: string[] } {
      const rowIds: string[] = [];
      const fullSupplierIds: string[] = [];

      this.carriers.forEach((carrier) => {
        if (this.selectedCarriers[carrier.supplierId]) {
          fullSupplierIds.push(carrier.supplierId);
          carrier.products.forEach((product) => rowIds.push(...product.rowIds));
        } else {
          carrier.products.forEach((product) => {
            if (this.selectedProducts[product.key]) {
              rowIds.push(...product.rowIds);
            }
          });
        }
      });

      return { rowIds, fullSupplierIds };
    },
    hasSelection(): boolean {
      return this.selection.rowIds.length > 0;
    },
    confirmationItems(): string[] {
      const items: string[] = [];
      this.carriers.forEach((carrier) => {
        if (this.selectedCarriers[carrier.supplierId]) {
          items.push(this.$t('proposal.products.remove.whole_carrier', { carrier: carrier.supplier }) as string);
        } else {
          carrier.products.forEach((product) => {
            if (this.selectedProducts[product.key]) {
              items.push(`${carrier.supplier} — ${product.label}`);
            }
          });
        }
      });
      return items;
    },
  },
  methods: {
    openModal(): void {
      this.selectedCarriers = {};
      this.selectedProducts = {};
      this.modalVisibility = true;
    },
    toggleProduct(key: string, event: Event): void {
      this.selectedProducts[key] = (event.target as HTMLInputElement).checked;
    },
    carrierCounts(carrier: RemoveCarrier): string {
      const outbound = carrier.products.filter((p) => p.tab === 'outbound').length;
      const returns = carrier.products.filter((p) => p.tab === 'returns').length;
      return this.$t('proposal.products.remove.counts', {
        outbound,
        returns,
        surcharges: carrier.surchargeCount,
      }) as string;
    },
    async handleRemove(): Promise<void> {
      await this.$store.dispatch(ProposalAction.REMOVE_PRODUCTS, this.selection);
      this.showConfirm = false;
      this.modalVisibility = false;
    },
  },
});
